import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Save, Music, Video, FileText, Image as ImageIcon, Link as LinkIcon, Award, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KINDS = [
  { id: "audio", label: "Audio", icon: Music },
  { id: "video", label: "Video", icon: Video },
  { id: "image", label: "Immagine", icon: ImageIcon },
  { id: "pdf", label: "PDF / Doc", icon: FileText },
  { id: "link", label: "Link esterno", icon: LinkIcon },
  { id: "release", label: "Release", icon: Award },
];
const KIND_ICON = Object.fromEntries(KINDS.map((k) => [k.id, k.icon]));
const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.id, k.label]));

const EMPTY = { artist_id: "", kind: "audio", title: "", description: "", url: "" };

export default function CRMMaterials() {
  const [artists, setArtists] = useState([]);
  const [selected, setSelected] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/artists").then((r) => {
      setArtists(r.data);
      if (r.data.length && !selected) setSelected(r.data[0].id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!selected) { setItems([]); setLoading(false); return; }
    setLoading(true);
    api.get(`/crm/materials?artist_id=${selected}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [selected]);

  const reload = () => {
    if (!selected) return;
    api.get(`/crm/materials?artist_id=${selected}`).then((r) => setItems(r.data));
  };

  const save = async () => {
    if (!editing.title) { toast.error("Titolo obbligatorio"); return; }
    if (!editing.artist_id) { toast.error("Seleziona un artista"); return; }
    try {
      await api.post("/crm/materials", editing);
      toast.success("Materiale aggiunto");
      setEditing(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo materiale?")) return;
    await api.delete(`/crm/materials/${id}`);
    reload();
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="crm-materials-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Roster</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Materiali Artisti</h1>
          <p className="text-[#0A0A0A]/60 mt-2 max-w-2xl">Documenti, audio, video e link consegnati agli artisti. Ogni artista vede automaticamente i propri materiali nella sua area riservata.</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY, artist_id: selected })}
          disabled={!selected}
          className="btn-cta disabled:opacity-50"
          data-testid="material-add-btn"
        >
          <Plus size={16} /> Nuovo materiale
        </button>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Artista</span>
          <select value={selected} onChange={(e) => setSelected(e.target.value)} className="mt-1 block min-w-[240px] border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="material-artist-select">
            <option value="">— Seleziona artista —</option>
            {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        {selected && (
          <span className="text-xs uppercase tracking-widest text-[#0A0A0A]/50">
            {items.length} materiali per <strong>{artists.find(a => a.id === selected)?.name}</strong>
          </span>
        )}
      </div>

      {!selected ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/20 p-12 text-center text-[#0A0A0A]/50">Seleziona un artista per vedere i suoi materiali.</div>
      ) : loading ? (
        <div className="text-[#0A0A0A]/50">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/20 p-12 text-center text-[#0A0A0A]/50" data-testid="materials-empty">
          Nessun materiale ancora per questo artista.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((m) => {
            const Icon = KIND_ICON[m.kind] || FileText;
            return (
              <div key={m.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-5" data-testid={`mat-${m.id}`}>
                <div className="flex items-start justify-between">
                  <Icon size={22} className="text-[#FF5A1F]" strokeWidth={1.5} />
                  <div className="flex gap-1">
                    {m.url && (
                      <a href={m.url} target="_blank" rel="noopener noreferrer" className="p-1 text-[#0A0A0A]/60 hover:text-[#FF5A1F]"><ExternalLink size={14} /></a>
                    )}
                    <button onClick={() => remove(m.id)} className="p-1 text-[#0A0A0A]/40 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </div>
                <span className="text-[9px] uppercase tracking-widest bg-[#0A0A0A] text-[#FAFAF7] px-1.5 py-0.5 font-bold mt-3 inline-block">{KIND_LABEL[m.kind] || m.kind}</span>
                <h3 className="mt-2 font-bold text-lg leading-tight">{m.title}</h3>
                {m.description && <p className="mt-2 text-sm text-[#0A0A0A]/70 leading-relaxed">{m.description}</p>}
                <div className="mt-3 text-[10px] uppercase tracking-widest text-[#0A0A0A]/40">
                  {new Date(m.created_at).toLocaleDateString("it-IT")} · {m.uploaded_by_name || "team"}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#FAFAF7] max-w-lg w-full max-h-[92vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex justify-between">
              <h3 className="font-display text-2xl uppercase">Nuovo materiale</h3>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Artista</span>
                <select value={editing.artist_id} onChange={(e) => setEditing({ ...editing, artist_id: e.target.value })} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="mat-edit-artist">
                  <option value="">—</option>
                  {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Tipo</span>
                <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="mat-edit-kind">
                  {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Titolo</span>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="mat-edit-title" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Descrizione (opzionale)</span>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm resize-none" data-testid="mat-edit-desc" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">URL (Dropbox / Drive / YouTube / Spotify / etc.)</span>
                <input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://..." className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm font-mono" data-testid="mat-edit-url" />
              </label>
            </div>
            <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline-dark">Annulla</button>
              <button onClick={save} className="btn-cta" data-testid="mat-save"><Save size={14} /> Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
